/**
 * MobileTradeCenter — Trades (LOCKED redesign).
 * Ref: design_handoff_mobile_ui/Trades Wizard.dc.html + Trades Pickers.dc.html
 *      (child TChip), handoff CLAUDE.md "LOCKED — Trade Center".
 *
 * Three primary tabs: Propose · Inbox · Sent.
 * Propose = a 4-step wizard (Team → They Give → You Give → Review + Analyzer) with
 * a numbered step rail; asset chips via TChip (player=blue / pick=purple / cash=green);
 * cash entry via a slide-up stepper sheet. Flush editorial — no rounded/bordered
 * cards. All colors via tokens; tints via withAlpha.
 *
 * Rule 11: every data path of the previous Trade Center is preserved — the desktop
 * endpoints (proposeTrade / analyzeTrade / respondToTrade / cancelTrade /
 * getMyTradeProposals / roster / picks / cash) and BOTH placement cascades
 * (propose-side + accept-side, via MobileDropDesignationModal + RosterCapacityUtils).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';
import MobilePlayerLink from '../components/MobilePlayerLink';
import MobileTableSectionHeader from '../components/MobileTableSectionHeader';
import MobileTeamLogo from '../components/MobileTeamLogo';
import { isPitcherPosition } from '../../../utils/playerClassification';

const C = dynastyTheme.tokens.colors;
const GROUND = C.neutral[950];   // page ground
const PANEL = C.ground.screen;   // dark inputs / footers / sheet bottoms
const BORDER = C.border;         // standard panel border
const HAIR = C.hairline;         // soft hairline
const GREEN = C.spark.up;        // trade-positive green
const RED = C.error;             // trade-negative red

// buildPlacement — convert a chosen slot option into the backend placement object.
// Incoming players are canonical (getTeamRosterCanonical) → ids from the canonical
// namespace ONLY (no degraded fallback; `?? null` surfaces an absent id). Backend
// action mapping (CC3): active→target_slot; bench/dl/minors→no slot/drop;
// replace→drop_league_player_id.
const buildPlacement = (player, opt) => ({
  incoming_player_mlb_id: player?.ids?.mlb ?? null,
  incoming_player_league_player_id: player?.ids?.league_player ?? null,
  action: opt.status,
  target_slot: opt.status === 'active' ? opt.slotId : null,
  drop_league_player_id: opt.status === 'replace' ? (opt.dropLeaguePlayerId ?? null) : null,
});

// buildSlotOptions — mirrors PositionAssignmentDropdown logic (NOT imported), built
// mobile-native. Eligible active position slots (natural + composite/flex CI/MI/UTIL/
// P/OF) with first-vacant slot id, then Bench, then DL (gated dl_slots>0), then Minors
// (gated minor_league_slots>0). Per option: currentlyFilled/maxSlots + canAssign.
const buildSlotOptions = (league, currentRoster, player) => {
  const rs = league?.roster_settings;
  if (!rs?.position_requirements || !Array.isArray(currentRoster)) return [];
  const elig = player?.league_context?.roster?.effective_eligibility
    || (player?.info?.position ? [player.info.position] : []);
  const benchSlots = rs.bench_slots || 0;
  const dlSlots = rs.dl_slots || 0;
  const minorSlots = rs.minor_league_slots || 0;

  const activeAssignments = {};
  const occupiedSlots = new Set();
  currentRoster.forEach((rp) => {
    const status = rp.league_context?.roster?.status;
    const position = rp.league_context?.roster?.position;
    if (status === 'active' && position) {
      activeAssignments[position.split('_')[0]] = (activeAssignments[position.split('_')[0]] || 0) + 1;
      occupiedSlots.add(position);
    }
  });
  const countStatus = (s) => currentRoster.filter((p) => p.league_context?.roster?.status === s).length;

  const isEligible = (pos) => {
    if (elig.includes(pos)) return true;
    if (pos === 'MI' && (elig.includes('2B') || elig.includes('SS'))) return true;
    if (pos === 'CI' && (elig.includes('1B') || elig.includes('3B'))) return true;
    if (pos === 'UTIL' && !elig.some(isPitcherPosition)) return true;
    if (pos === 'P' && ['SP', 'RP'].some((p) => elig.includes(p))) return true;
    if (pos === 'OF' && ['LF', 'CF', 'RF', 'OF'].some((p) => elig.includes(p))) return true;
    return false;
  };

  const opts = [];
  Object.entries(rs.position_requirements).forEach(([position, config]) => {
    if (!isEligible(position)) return;
    const maxSlots = config?.slots || 0;
    const currentlyFilled = activeAssignments[position] || 0;
    const available = maxSlots - currentlyFilled;
    let slotId = null;
    for (let i = 0; i < maxSlots && available > 0; i++) {
      if (!occupiedSlots.has(`${position}_${i}`)) { slotId = `${position}_${i}`; break; }
    }
    opts.push({ status: 'active', position, label: position, badge: position, maxSlots, currentlyFilled, canAssign: available > 0, slotId });
  });
  if (benchSlots > 0) opts.push({ status: 'bench', position: 'bench', label: 'Bench', badge: 'BN', maxSlots: benchSlots, currentlyFilled: countStatus('bench'), canAssign: countStatus('bench') < benchSlots, slotId: null });
  if (dlSlots > 0) opts.push({ status: 'dl', position: 'dl', label: 'Disabled List', badge: 'DL', maxSlots: dlSlots, currentlyFilled: countStatus('dl'), canAssign: countStatus('dl') < dlSlots, slotId: null });
  if (minorSlots > 0) opts.push({ status: 'minors', position: 'minors', label: 'Minor Leagues', badge: 'MIN', maxSlots: minorSlots, currentlyFilled: countStatus('minors'), canAssign: countStatus('minors') < minorSlots, slotId: null, note: 'No contract until called up' });
  return opts;
};

// Auto-preselect: direct position match → first available active → Bench → Minors.
const preselectSlot = (opts, player) => {
  const playerPos = player?.info?.position;
  const actives = opts.filter((o) => o.status === 'active');
  return (
    actives.find((o) => o.position === playerPos && o.canAssign)
    || actives.find((o) => o.canAssign)
    || opts.find((o) => o.status === 'bench' && o.canAssign)
    || opts.find((o) => o.status === 'minors' && o.canAssign)
    || null
  );
};

// MobileSlotPicker — slide-up "Place {player}" sheet. Option rows = badge + label
// left, filled/max right; selected = gold border + tp-soft fill; full = muted/disabled.
// Auto-preselects the best slot; Confirm returns the chosen option to the parent
// (which builds the placement + steps to the next incoming player).
const MobileSlotPicker = ({ player, league, currentRoster, stepLabel, committing, onConfirm, onClose }) => {
  const opts = useMemo(() => buildSlotOptions(league, currentRoster, player), [league, currentRoster, player]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { setSelected(preselectSlot(opts, player)); }, [opts, player]);
  const name = player?.info?.full_name || player?.player_name || player?.name || 'player';
  const isSel = (o) => selected && selected.status === o.status && selected.position === o.position && selected.slotId === o.slotId;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1300, background: withAlpha(C.black || GROUND, 0.55), display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: `linear-gradient(180deg, ${C.ground.screenTop}, ${C.ground.screen})`, borderTop: `1px solid ${BORDER}`, borderRadius: '16px 16px 0 0' }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '7px 0 6px' }}><div style={{ width: 36, height: 3, borderRadius: 2, background: C.coolGray[720] }} /></div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px 9px' }}>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 900, color: C.textHi, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Place {name}</span>
            {stepLabel && <span style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>{stepLabel}</span>}
          </span>
          <span onClick={onClose} style={{ fontSize: 13, color: C.muted, cursor: 'pointer' }}>✕</span>
        </div>
        <div className="tc-ns" style={{ overflowY: 'auto', padding: '0 14px' }}>
          {opts.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: C.muted, fontSize: 12 }}>No roster slots available</div>
            : opts.map((o, i) => {
              const sel = isSel(o);
              const disabled = !o.canAssign;
              return (
                <div key={`${o.status}-${o.position}-${i}`} onClick={() => o.canAssign && setSelected(o)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', cursor: disabled ? 'default' : 'pointer',
                  borderTop: `1px solid ${HAIR}`, opacity: disabled ? 0.4 : 1,
                  background: sel ? C.primaryTint.t13 : 'transparent', border: sel ? `1px solid ${C.primary}` : '1px solid transparent', borderRadius: sel ? 4 : 0,
                }}>
                  <span style={{ width: 26, height: 26, borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: sel ? C.primary : C.muted, background: withAlpha(C.goldBase, sel ? 0.18 : 0.08), border: `1px solid ${sel ? withAlpha(C.goldBase, 0.4) : BORDER}` }}>{o.badge}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: disabled ? C.muted : C.textHi }}>{o.label}</span>
                    {o.note && <span style={{ display: 'block', fontSize: 8.5, color: C.warning }}>{o.note}</span>}
                  </span>
                  <span style={{ fontSize: 11, color: o.canAssign ? C.green.light : C.spark.down, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{o.currentlyFilled}/{o.maxSlots}</span>
                </div>
              );
            })}
        </div>
        <div style={{ padding: '12px 14px', borderTop: `1px solid ${BORDER}` }}>
          <button onClick={() => selected && onConfirm(selected)} disabled={!selected || committing} style={{
            display: 'block', width: '100%', textAlign: 'center', background: C.primary, color: GROUND, borderRadius: 5, padding: '11px 0', fontSize: 12, fontWeight: 800, border: 'none',
            cursor: (!selected || committing) ? 'default' : 'pointer', opacity: (!selected || committing) ? 0.4 : 1,
          }}>{committing ? 'Placing…' : selected ? `Place in ${selected.label}` : 'Select a slot'}</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TChip — asset chip (player blue / pick purple / cash green). Matches TChip.dc.html.
// ============================================================================
const TCHIP = {
  player: { bg: withAlpha(C.blue, 0.12), col: C.blueLight, bd: withAlpha(C.blue, 0.3) },
  pick:   { bg: withAlpha(C.purple, 0.12), col: C.purpleLighter, bd: withAlpha(C.purple, 0.3) },
  cash:   { bg: withAlpha(GREEN, 0.12), col: C.green.light, bd: withAlpha(GREEN, 0.3) },
};
const TChip = ({ type, label, onRemove, onClick, suffix }) => {
  const s = TCHIP[type] || TCHIP.player;
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '2px 6px', borderRadius: 3, fontSize: 9.5, fontWeight: 700,
        whiteSpace: 'nowrap', background: s.bg, color: s.col,
        border: `0.5px solid ${s.bd}`, cursor: (onClick || onRemove) ? 'pointer' : 'default',
      }}
    >
      {label}{suffix ? <span style={{ opacity: 0.85 }}>{suffix}</span> : null}
      {onRemove && (
        <span onClick={(e) => { e.stopPropagation(); onRemove(); }} style={{ fontSize: 10, lineHeight: 1, opacity: 0.85 }}>✕</span>
      )}
    </span>
  );
};

// Mini section label (steps 2/3 — green/gold/purple 9px caps, no bar; per mock).
const MiniLabel = ({ color, children, style }) => (
  <div style={{ fontSize: 9, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.4px', color, ...style }}>
    {children}
  </div>
);

// ============================================================================
// Canonical asset accessors — shared by the wizard roster rows AND the enriched
// Inbox/Sent trade assets. Both are canonical players (getTeamRosterCanonical /
// get_player_for_trade_display), so these read the canonical namespaces ONLY — no
// flat/legacy substitutes. assetPos reads BOTH canonical position keys (roster uses
// info.primary_position; enriched trade players use info.position) — two canonical
// keys, NOT a flat fallback. (?? defaults are true absent-defaults, not alt-sources.)
// ============================================================================
const getName = (p) => p.info?.full_name ?? 'Unknown';
const getPos = (p) => p.info?.primary_position ?? p.info?.position ?? '';
const getSalNum = (p) => Number(p.league_context?.financial?.contract_salary ?? 0) || 0;
const getSal = (p) => { const s = p.league_context?.financial?.contract_salary; return s != null ? `$${s}` : ''; };
const getMlb = (p) => p.ids?.mlb;
const getPickLabel = (pk) => `${pk.year ?? '?'} ${pk.pick_type === 'ml' ? 'ML ' : ''}R${pk.round ?? '?'}`;

// ============================================================================
// Step rail — Team → They Give → You Give → Review (per Trades Wizard.dc.html)
// ============================================================================
const RAIL = ['Team', 'They Give', 'You Give', 'Review'];
const StepRail = ({ step }) => (
  <div style={{ display: 'flex', alignItems: 'center', padding: '9px 12px', borderBottom: `1px solid ${BORDER}` }}>
    {RAIL.map((label, i) => {
      const n = i + 1;
      const state = n < step ? 'done' : n === step ? 'on' : 'other';
      const bg = state === 'on' ? C.primary : state === 'done' ? C.primaryTint.t15 : PANEL;
      const col = state === 'on' ? GROUND : state === 'done' ? C.primary : C.muted;
      const bd = state === 'on' ? C.primary : state === 'done' ? withAlpha(C.goldBase, 0.32) : BORDER;
      const tcol = state === 'other' ? C.muted : C.textHi;
      return (
        <React.Fragment key={n}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{
              width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 9, fontWeight: 900, background: bg, color: col,
              border: `1px solid ${bd}`,
            }}>{n}</span>
            <span style={{ fontSize: 8.5, fontWeight: 700, color: tcol }}>{label}</span>
          </span>
          {i < 3 && <span style={{ flex: 1, height: 1, background: BORDER, margin: '0 5px', minWidth: 8 }} />}
        </React.Fragment>
      );
    })}
  </div>
);

// Wizard footer + buttons (per .footer / .btn / .btno)
const WizFooter = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderTop: `1px solid ${BORDER}`, background: PANEL }}>{children}</div>
);
const PrimaryBtn = ({ onClick, disabled, children }) => (
  <button onClick={onClick} disabled={disabled} style={{
    fontSize: 11, fontWeight: 800, color: GROUND, background: C.primary, borderRadius: 4,
    padding: '8px 16px', border: 'none', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
  }}>{children}</button>
);
const GhostBtn = ({ onClick, children }) => (
  <button onClick={onClick} style={{
    fontSize: 11, fontWeight: 700, color: C.muted, border: `1px solid ${BORDER}`, borderRadius: 4,
    padding: '7px 12px', background: 'none', cursor: 'pointer',
  }}>{children}</button>
);

const searchBox = {
  display: 'flex', alignItems: 'center', gap: 6, background: PANEL,
  border: `1px solid ${BORDER}`, borderRadius: 3, padding: '6px 9px',
};
const SearchIcon = () => (
  <span style={{ width: 13, height: 13, color: C.muted, flexShrink: 0, display: 'inline-flex' }}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
  </span>
);

// ============================================================================
// Cash / FAAB stepper sheet (slide-up; per Trades Pickers.dc.html C)
// ============================================================================
const CashSheet = ({ title, available, value, onChange, onConfirm, onClose }) => {
  const max = Math.max(0, Math.min(available ?? 0, 99));
  const [v, setV] = useState(Math.min(value || 0, max));
  const pct = max > 0 ? Math.round((v / max) * 100) : 0;
  const circle = (sym, on, fn) => (
    <span onClick={fn} style={{
      width: 42, height: 42, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 22, cursor: 'pointer',
      border: `1px solid ${on ? withAlpha(C.goldBase, 0.32) : BORDER}`,
      background: on ? C.primaryTint.t13 : PANEL, color: on ? C.primary : C.coolGray[150],
    }}>{sym}</span>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: withAlpha(C.black || GROUND, 0.55), display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', background: `linear-gradient(180deg, ${C.ground.screenTop}, ${C.ground.screen})`,
        borderTop: `1px solid ${BORDER}`, borderRadius: '16px 16px 0 0', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '7px 0 6px' }}><div style={{ width: 36, height: 3, borderRadius: 2, background: C.coolGray[720] }} /></div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px 9px' }}>
          <span style={{ fontSize: 14, fontWeight: 900, color: C.textHi }}>{title}</span>
          <span onClick={onClose} style={{ fontSize: 12, color: C.muted, cursor: 'pointer' }}>✕</span>
        </div>
        <div style={{ padding: '6px 18px 0', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.mutedLight, marginBottom: 18 }}>
            <span>Available FAAB <span style={{ color: GREEN, fontWeight: 700 }}>${available ?? 0}</span></span>
            <span>Max / trade <span style={{ color: C.coolGray[150], fontWeight: 700 }}>${max}</span></span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 6 }}>
            {circle('−', false, () => setV(p => Math.max(0, p - 5)))}
            <div style={{ textAlign: 'center', minWidth: 96 }}>
              <div style={{ fontSize: 38, fontWeight: 900, color: C.primary, lineHeight: 1 }}>${v}</div>
              <div style={{ fontSize: 9, color: C.muted }}>FAAB</div>
            </div>
            {circle('+', true, () => setV(p => Math.min(max, p + 5)))}
          </div>
          <div style={{ height: 6, background: PANEL, borderRadius: 4, overflow: 'hidden', margin: '14px 0 8px' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: C.primary, borderRadius: 4 }} />
          </div>
          <div style={{ display: 'flex', gap: 7, justifyContent: 'center', marginTop: 6 }}>
            {[5, 10, max].map((q, i) => (
              <span key={i} onClick={() => setV(Math.min(q, max))} style={{
                fontSize: 9.5, fontWeight: 700, borderRadius: 3, padding: '4px 9px', cursor: 'pointer',
                background: PANEL, border: `1px solid ${BORDER}`, color: C.coolGray[150],
              }}>${q}{i === 2 ? ' max' : ''}</span>
            ))}
          </div>
        </div>
        <div style={{ padding: '12px 14px', borderTop: `1px solid ${BORDER}`, marginTop: 10 }}>
          <span onClick={() => { onChange(v); onConfirm && onConfirm(); }} style={{
            display: 'block', textAlign: 'center', background: C.primary, color: GROUND, borderRadius: 5,
            padding: '11px 0', fontSize: 12, fontWeight: 800, cursor: 'pointer',
          }}>Add ${v} FAAB</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TradeAnalyzerSheet — user-triggered slide-up analyzer. Reads the REAL nested
// analyzeTrade response shape (CC3 RCA): summary.winner/point_advantage,
// salary_cap_impact.{proposing,receiving}_team.{current_cap,post_trade_cap,change},
// standings_projection.{...}.{current_rank,point_change,projected_points},
// roster_composition, player_comparison + scoring_categories, warnings[].
// Never renders a default/"Even" verdict before the call resolves.
// ============================================================================
const rowKV = (lbl, val) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
    <span style={{ color: C.mutedLight }}>{lbl}</span><span style={{ color: C.coolGray[150] }}>{val}</span>
  </div>
);
const signed = (n, d = 1) => `${n > 0 ? '+' : ''}${Number(n).toFixed(d)}`;
const deltaColor = (n) => (n > 0 ? GREEN : n < 0 ? C.spark.down : C.coolGray[150]);

const TradeAnalyzerSheet = ({ open, loading, analysis, onClose }) => {
  if (!open) return null;
  const a = analysis || {};
  const failed = a.success === false;
  const summary = a.summary || {};
  const cap = a.salary_cap_impact || {};
  const st = a.standings_projection || {};
  const rc = a.roster_composition || null;
  const warnings = a.warnings || [];
  const propName = a.proposing_team_name || 'Proposing team';
  const recvName = a.receiving_team_name || 'Receiving team';

  // Verdict from the nested summary (NOT flat net_value_difference).
  const winner = summary.winner;
  const adv = Number(summary.point_advantage ?? 0);
  let vcol = C.primary, varrow = '', vtext = 'Even Trade', vsub = 'fairly balanced';
  if (winner === 'proposing_team') { vcol = GREEN; varrow = '▲'; vtext = `${propName} Wins`; vsub = `Advantage: ${adv.toFixed(1)} pts`; }
  else if (winner === 'receiving_team') { vcol = C.spark.down; varrow = '▼'; vtext = `${recvName} Wins`; vsub = `They gain ${adv.toFixed(1)} pts`; }

  const capSide = (side, name) => {
    const s = cap[side]; if (!s) return null;
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', color: C.muted, marginBottom: 2 }}>{name}</div>
        {s.current_cap != null && rowKV('Cap now → after', <span>${s.current_cap} → <span style={{ color: deltaColor(-(s.change ?? 0)) }}>${s.post_trade_cap}</span></span>)}
        {s.change != null && rowKV('Change', <span style={{ color: deltaColor(-s.change) }}>{signed(s.change, 0).replace('+', '+$').replace('-', '-$')}</span>)}
      </div>
    );
  };
  const stSide = (side, name) => {
    const s = st[side]; if (!s) return null;
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', color: C.muted, marginBottom: 2 }}>{name}</div>
        {s.current_rank != null && rowKV('Rank', <span>#{s.current_rank}</span>)}
        {s.point_change != null && rowKV('Proj pts change', <span style={{ color: deltaColor(s.point_change) }}>{signed(s.point_change)}</span>)}
        {s.projected_points != null && rowKV('Projected pts', <span>{Number(s.projected_points).toFixed(1)}</span>)}
      </div>
    );
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1300, background: withAlpha(C.black || GROUND, 0.55), display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: `linear-gradient(180deg, ${C.ground.screenTop}, ${C.ground.screen})`, borderTop: `1px solid ${BORDER}`, borderRadius: '16px 16px 0 0' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 800, color: C.primary }}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /><path d="M17 7h4v4" /></svg>
            Trade Analysis
          </span>
          <span onClick={onClose} style={{ fontSize: 13, color: C.muted, cursor: 'pointer' }}>✕</span>
        </div>

        <div className="tc-ns" style={{ overflowY: 'auto', padding: '12px 14px 18px' }}>
          {loading ? (
            <div style={{ padding: '32px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.textHi, marginBottom: 4 }}>Analyzing trade…</div>
              <div style={{ fontSize: 11, color: C.muted }}>Calculating impact on salary cap, roster, and standings</div>
            </div>
          ) : failed ? (
            <div style={{ padding: '24px 12px', textAlign: 'center', color: C.spark.down, fontSize: 12 }}>{a.detail || a.error || 'Trade analysis failed.'}</div>
          ) : !analysis ? null : (
            <>
              {/* Verdict */}
              <div style={{ background: withAlpha(vcol, 0.08), border: `1px solid ${withAlpha(vcol, 0.25)}`, borderRadius: 4, padding: 10, textAlign: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 900, color: vcol }}>{varrow && <span style={{ marginRight: 4 }}>{varrow}</span>}{vtext}</div>
                <div style={{ fontSize: 10, color: C.mutedLight }}>{vsub}</div>
              </div>

              {/* Salary cap */}
              {(cap.proposing_team || cap.receiving_team) && (<>
                <MobileTableSectionHeader label="Salary-Cap Impact" color={C.blue} barColor={C.blue} />
                <div style={{ marginBottom: 8 }}>{capSide('proposing_team', propName)}{capSide('receiving_team', recvName)}</div>
              </>)}

              {/* Roster composition (position-count deltas; defensive render) */}
              {rc && (<>
                <MobileTableSectionHeader label="Roster Composition" color={C.blue} barColor={C.blue} />
                <div style={{ marginBottom: 8 }}>
                  {Object.entries(rc).map(([side, comp]) => (
                    <div key={side} style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', color: C.muted, marginBottom: 2 }}>{side === 'proposing_team' ? propName : side === 'receiving_team' ? recvName : side}</div>
                      {comp && typeof comp === 'object' && Object.entries(comp).map(([pos, v]) => {
                        const d = (v && typeof v === 'object') ? (v.change ?? v.delta) : v;
                        return rowKV(pos, (typeof d === 'number') ? <span style={{ color: deltaColor(d) }}>{signed(d, 0)}</span> : String(d));
                      })}
                    </div>
                  ))}
                </div>
              </>)}

              {/* Player stats comparison (player_comparison + scoring_categories; defensive) */}
              {Array.isArray(a.player_comparison) && a.player_comparison.length > 0 && (<>
                <MobileTableSectionHeader label="Player Stats" color={C.blue} barColor={C.blue} />
                <div style={{ marginBottom: 8 }}>
                  {a.player_comparison.map((p, i) => rowKV(p.name || p.player_name || `Player ${i + 1}`, <span style={{ color: deltaColor(p.value_change ?? p.delta ?? 0) }}>{(p.value_change ?? p.delta) != null ? signed(p.value_change ?? p.delta) : ''}</span>))}
                </div>
              </>)}

              {/* Standings */}
              {(st.proposing_team || st.receiving_team) && (<>
                <MobileTableSectionHeader label="Standings Projection" color={C.blue} barColor={C.blue} />
                <div style={{ marginBottom: 8 }}>{stSide('proposing_team', propName)}{stSide('receiving_team', recvName)}</div>
              </>)}

              {/* Warnings */}
              {warnings.length > 0 && (<>
                <MobileTableSectionHeader label="Warnings" color={C.warning} barColor={C.warning} />
                <div style={{ fontSize: 10, color: C.amber[300] }}>
                  {warnings.map((w, i) => <div key={i} style={{ marginBottom: 2 }}>• {typeof w === 'string' ? w : (w.message || w.text || JSON.stringify(w))}</div>)}
                </div>
              </>)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TAB 1: PROPOSE — 4-step wizard
// ============================================================================
const ProposeWizard = ({ leagueId, userTeamId, teams, onPlayerClick, league, initialPackage, onPrefillConsumed }) => {
  const [step, setStep] = useState(1);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [iGive, setIGive] = useState({ players: [], picks: [], cash: 0 });
  const [iGet, setIGet] = useState({ players: [], picks: [], cash: 0 });
  const [prefillNote, setPrefillNote] = useState(null);
  // Pending optimizer prefill: the package player_ids are MLB ids (package_generator
  // _build_player_summary → 'player_id': ids.mlb). The wizard's assets are CANONICAL roster
  // objects (getTeamRosterCanonical), so we can't drop the flat summaries straight into
  // iGive/iGet — we hold the package here and map its ids to real roster objects once the two
  // rosters load (Rule 9: no flat-field masquerade; the chips and downstream analysis need the
  // canonical shape). Two-team prefills fully; a three-team cycle can't be drawn in the
  // two-party wizard, so we prefill the user's own leg only and flag it (documented deviation).
  const [pendingPrefill, setPendingPrefill] = useState(null);

  // DD-MOBILE-OPTIMIZER-ACTIONS-0724: phase 1 — select the counterparty (triggers theirRoster
  // load) and jump into the wizard. Consumed once so a back-navigation doesn't re-fire.
  useEffect(() => {
    if (!initialPackage) return;
    const pkg = initialPackage;
    const isThree = pkg.trade_type === 'three_team';
    const opp = (pkg.teams_involved || []).find((t) => t !== (pkg.user_team_id || userTeamId));
    if (opp) setSelectedTeamId(opp);
    setPendingPrefill(pkg);
    setStep(2);
    setPrefillNote(isThree
      ? 'This was a 3-team cycle in the optimizer; the Trade Center shows your leg only — review before proposing.'
      : 'Pre-filled from the optimizer — review and propose.');
    if (onPrefillConsumed) onPrefillConsumed();
  }, [initialPackage, userTeamId, onPrefillConsumed]);
  const [cashGive, setCashGive] = useState(0);
  const [cashGet, setCashGet] = useState(0);
  const [notes, setNotes] = useState('');
  const [myRoster, setMyRoster] = useState([]);
  const [theirRoster, setTheirRoster] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [theirPicks, setTheirPicks] = useState([]);
  const [myCash, setMyCash] = useState(0);
  const [theirCash, setTheirCash] = useState(0);
  const [search, setSearch] = useState('');
  const [posFilter, setPosFilter] = useState('All');
  const [submitting, setSubmitting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzerOpen, setAnalyzerOpen] = useState(false); // user-triggered sheet
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [cashSheet, setCashSheet] = useState(null); // 'give' | 'get' | null

  // ---- data loads (PRESERVED) ----
  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getMyRosterCanonical(leagueId)
      .then(res => setMyRoster((res?.players || []).filter(p => !p.accrued_info)))
      .catch(() => setMyRoster([]));
  }, [leagueId]);

  useEffect(() => {
    if (!leagueId || !userTeamId) return;
    leaguesAPI.getTeamDraftPicks(leagueId, userTeamId, true) // §4.5.24 tradeable only
      .then(res => setMyPicks(res?.picks ?? []))
      .catch(() => setMyPicks([]));
    leaguesAPI.getTeamCashBalance(leagueId, userTeamId)
      .then(res => setMyCash(res?.cash_balance ?? 0))
      .catch(() => setMyCash(0));
  }, [leagueId, userTeamId]);

  useEffect(() => {
    if (!leagueId || !selectedTeamId) { setTheirRoster([]); setTheirPicks([]); setTheirCash(0); return; }
    setLoadingRoster(true);
    Promise.all([
      leaguesAPI.getTeamRosterCanonical(leagueId, selectedTeamId).catch(() => ({ players: [] })),
      leaguesAPI.getTeamDraftPicks(leagueId, selectedTeamId, true).catch(() => ({ picks: [] })), // §4.5.24 tradeable only
      leaguesAPI.getTeamCashBalance(leagueId, selectedTeamId).catch(() => ({ balance: 0 })),
    ]).then(([rosterRes, picksRes, cashRes]) => {
      setTheirRoster((rosterRes?.players || []).filter(p => !p.accrued_info));
      setTheirPicks(picksRes?.picks ?? []);
      setTheirCash(cashRes?.cash_balance ?? 0);
    }).finally(() => setLoadingRoster(false));
  }, [leagueId, selectedTeamId]);

  // DD-MOBILE-OPTIMIZER-ACTIONS-0724: phase 2 — once both rosters are in, map the package's MLB
  // player_ids to the CANONICAL roster objects the wizard operates on. I-give from user_sends
  // matched in MY roster; I-get from user_receives matched in THEIRS. Picks matched by pick_id;
  // cash carried through. Runs only after the loads settle, then consumes pendingPrefill so it
  // fires exactly once. A summary that finds no roster match is dropped (fail-visible: the note
  // says "review" — the user sees the real, matched assets, never a phantom flat chip).
  useEffect(() => {
    if (!pendingPrefill) return;
    if (loadingRoster) return;
    if (!myRoster.length) return;                          // my roster not loaded yet
    if (selectedTeamId && !theirRoster.length) return;     // counterparty roster not loaded yet
    const pkg = pendingPrefill;
    const matchPlayers = (summaries, roster) => {
      const byMlb = new Map(roster.map((p) => [p.ids?.mlb, p]));
      return (summaries || []).map((s) => byMlb.get(s.player_id)).filter(Boolean);
    };
    const matchPicks = (summaries, picks) => {
      const byId = new Map(picks.map((pk) => [pk.pick_id, pk]));
      return (summaries || []).map((s) => byId.get(s.pick_id)).filter(Boolean);
    };
    setIGive({
      players: matchPlayers(pkg.user_sends?.players, myRoster),
      picks: matchPicks(pkg.user_sends?.picks, myPicks),
      cash: 0,
    });
    setIGet({
      players: matchPlayers(pkg.user_receives?.players, theirRoster),
      picks: matchPicks(pkg.user_receives?.picks, theirPicks),
      cash: 0,
    });
    setCashGive(Number(pkg.user_sends?.cash) || 0);
    setCashGet(Number(pkg.user_receives?.cash) || 0);
    setAnalysis(null);
    setPendingPrefill(null);
  }, [pendingPrefill, loadingRoster, myRoster, theirRoster, myPicks, theirPicks, selectedTeamId]);

  // ---- selection helpers (PRESERVED toggle semantics) ----
  // Canonical league-player id (UUID) — ids.league_player, the SAME field
  // MobileDropDesignationModal / MobileRoster read and that proposeTrade's player
  // list expects. Rostered canonical players always carry it; no top-level
  // league_player_id/id on these objects (that older shape returned undefined for
  // EVERY row → one selection keyed/checked them all). No fallback (Bible rule).
  const playerId = (p) => p.ids?.league_player;
  const pickId = (pk) => pk.pick_id; // canonical pick id (getTeamDraftPicks / trade picks)
  const isPlayerIn = (deal, id) => deal.players.some(p => playerId(p) === id);
  const isPickIn = (deal, id) => deal.picks.some(p => pickId(p) === id);

  const togglePlayer = (setDeal, player) => {
    const id = playerId(player);
    setDeal(prev => isPlayerIn(prev, id)
      ? { ...prev, players: prev.players.filter(p => playerId(p) !== id) }
      : { ...prev, players: [...prev.players, player] });
  };
  const togglePick = (setDeal, pick) => {
    const id = pickId(pick);
    setDeal(prev => isPickIn(prev, id)
      ? { ...prev, picks: prev.picks.filter(p => pickId(p) !== id) }
      : { ...prev, picks: [...prev.picks, pick] });
  };

  // getName / getPos / getSalNum / getSal / getMlb / getPickLabel are the shared
  // module-level canonical accessors (above).
  const pickTraded = (pk) => pk.original_owner_name && pk.original_owner_name !== pk.team_name;

  // ---- placement cascade (slot-picker) ----
  const [placementCascade, setPlacementCascade] = useState({ active: false, currentIdx: 0, accumulated: [] });

  const buildTradeData = () => ({
    receiving_team_id: selectedTeamId,
    proposing_team_gives: {
      players: iGive.players.map(playerId),
      picks: iGive.picks.map(p => ({ pick_id: pickId(p), pick_type: p.pick_type || 'regular' })),
      cash: parseInt(cashGive) || 0,
    },
    receiving_team_gives: {
      players: iGet.players.map(playerId),
      picks: iGet.picks.map(p => ({ pick_id: pickId(p), pick_type: p.pick_type || 'regular' })),
      cash: parseInt(cashGet) || 0,
    },
  });

  const submitTradeWithPlacements = async (proposerPlacements) => {
    setSubmitting(true);
    try {
      await leaguesAPI.proposeTrade(leagueId, {
        ...buildTradeData(),
        notes,
        proposer_placements: proposerPlacements && proposerPlacements.length > 0 ? proposerPlacements : null,
      });
      // reset
      setIGive({ players: [], picks: [], cash: 0 });
      setIGet({ players: [], picks: [], cash: 0 });
      setCashGive(0); setCashGet(0); setNotes(''); setAnalysis(null); setSelectedTeamId(''); setStep(1);
    } catch (err) {
      console.error('Propose trade error:', err);
    } finally { setSubmitting(false); }
  };

  const handlePropose = async () => {
    if (!selectedTeamId) return;
    if (iGet.players.length > 0) { setPlacementCascade({ active: true, currentIdx: 0, accumulated: [] }); return; }
    await submitTradeWithPlacements(null);
  };
  const handleCascadeConfirm = async (option) => {
    const incomingPlayer = iGet.players[placementCascade.currentIdx];
    const placement = buildPlacement(incomingPlayer, option);
    const newAccumulated = [...placementCascade.accumulated, placement];
    const nextIdx = placementCascade.currentIdx + 1;
    if (nextIdx >= iGet.players.length) {
      setPlacementCascade({ active: false, currentIdx: 0, accumulated: [] });
      await submitTradeWithPlacements(newAccumulated);
    } else {
      setPlacementCascade({ active: true, currentIdx: nextIdx, accumulated: newAccumulated });
    }
  };
  const handleCascadeClose = () => setPlacementCascade({ active: false, currentIdx: 0, accumulated: [] });

  // User-triggered ONLY (no auto-fire): opens the sheet, shows loading, then renders.
  const runAnalyze = useCallback(async () => {
    if (!selectedTeamId) return;
    setAnalysis(null);
    setAnalyzing(true);
    setAnalyzerOpen(true);
    try {
      const res = await leaguesAPI.analyzeTrade(leagueId, buildTradeData());
      setAnalysis(res);
    } catch (err) { console.error('Analyze trade error:', err); setAnalysis({ success: false, detail: 'Trade analysis failed.' }); }
    finally { setAnalyzing(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, selectedTeamId, iGive, iGet, cashGive, cashGet]);

  const giveCount = iGive.players.length + iGive.picks.length + (parseInt(cashGive) > 0 ? 1 : 0);
  const getCount = iGet.players.length + iGet.picks.length + (parseInt(cashGet) > 0 ? 1 : 0);
  const hasAssets = giveCount > 0 || getCount > 0;

  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);
  const POSES = ['All', 'C', '1B', '2B', 'SS', '3B', 'OF', 'SP'];
  // Position ELIGIBILITY — CANONICAL ONLY: league_context.roster.effective_eligibility.
  // This is guaranteed injected by the backend get_team_roster →
  // _inject_effective_eligibility (the path behind BOTH getMyRosterCanonical and
  // getTeamRosterCanonical), so the field is always present on these rosters. Per the
  // Bible no-fallback rule there is NO degraded substitute (default_eligibility /
  // primary position): if it were ever absent the player correctly drops out of
  // position-filtered views — surfacing the upstream injection failure rather than
  // papering over it. Matches MobilePositionAssignmentModal / MobileRosterPlanModal
  // (`effective_eligibility || []`).
  const getEligibility = (p) => {
    const raw = p.league_context?.roster?.effective_eligibility || [];
    return (Array.isArray(raw) ? raw : []).map(x => String(x).toUpperCase());
  };
  const eligibleAt = (p, pos) => {
    if (pos === 'All') return true;
    const elig = getEligibility(p);
    // pitcher chip "SP" matches the pitcher eligibility tokens (P / SP / RP).
    if (pos === 'SP') return elig.some(e => e === 'SP' || e === 'P' || e === 'RP');
    return elig.includes(pos);
  };
  // NOTE: filters the player ROSTER array ONLY — the picks & cash section below is
  // rendered from `picks` (never passed through this filter).
  const filterRoster = (list) => list.filter(p => {
    if (search && !getName(p).toLowerCase().includes(search.toLowerCase())) return false;
    if (posFilter !== 'All' && !eligibleAt(p, posFilter)) return false;
    return true;
  });

  // ---- shared give/get step body ----
  const GiveGetStep = ({ side }) => {
    const isGet = side === 'get';
    const deal = isGet ? iGet : iGive;
    const setDeal = isGet ? setIGet : setIGive;
    const roster = isGet ? theirRoster : myRoster;
    const picks = isGet ? theirPicks : myPicks;
    const cashVal = isGet ? cashGet : cashGive;
    const setCash = isGet ? setCashGet : setCashGive;
    const salary = deal.players.reduce((s, p) => s + getSalNum(p), 0);
    const trayColor = isGet ? GREEN : RED;
    return (
      <div style={{ padding: '11px 12px' }}>
        <MiniLabel color={trayColor} style={{ marginBottom: 5 }}>{isGet ? "You'll receive" : "You'll send"}</MiniLabel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 5, minHeight: 18 }}>
          {deal.players.map(p => <TChip key={playerId(p)} type="player" label={`${getPos(p)} ${getName(p)} ${getSal(p)}`} onRemove={() => togglePlayer(setDeal, p)} />)}
          {deal.picks.map(pk => <TChip key={pickId(pk)} type="pick" label={getPickLabel(pk)} onRemove={() => togglePick(setDeal, pk)} />)}
          {parseInt(cashVal) > 0 && <TChip type="cash" label={`$${cashVal} FAAB`} onRemove={() => setCash(0)} />}
          {!deal.players.length && !deal.picks.length && !(parseInt(cashVal) > 0) && <span style={{ fontSize: 10, color: C.muted }}>Nothing selected yet</span>}
        </div>
        <div style={{ fontSize: 9, color: C.muted, marginBottom: 12 }}>Salary {isGet ? 'in' : 'out'} ${salary}</div>

        {/* search + position chips (per Trades Pickers.dc.html, inline) */}
        <div style={{ ...searchBox, marginBottom: 7 }}>
          <SearchIcon />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={isGet ? 'Search their roster…' : 'Search your roster…'} style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: C.textHi, fontSize: 11 }} />
        </div>
        <div className="tc-ns" style={{ overflowX: 'auto', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 5, width: 'max-content' }}>
            {POSES.map(pf => (
              <span key={pf} onClick={() => setPosFilter(pf)} style={{
                fontSize: 9.5, fontWeight: posFilter === pf ? 800 : 700, borderRadius: 3, padding: '4px 9px', cursor: 'pointer', whiteSpace: 'nowrap',
                background: posFilter === pf ? C.primary : PANEL, color: posFilter === pf ? GROUND : C.muted,
                border: posFilter === pf ? 'none' : `1px solid ${BORDER}`,
              }}>{pf}</span>
            ))}
          </div>
        </div>

        <MiniLabel color={C.primary} style={{ marginBottom: 6 }}>{isGet ? 'Their roster' : 'Your roster'}</MiniLabel>
        {loadingRoster && isGet ? (
          <div style={{ padding: 16, textAlign: 'center', color: C.muted, fontSize: 11 }}>Loading roster…</div>
        ) : filterRoster(roster).length === 0 ? (
          <div style={{ padding: 12, textAlign: 'center', color: C.muted, fontSize: 11 }}>No players</div>
        ) : filterRoster(roster).map(p => {
          const inDeal = isPlayerIn(deal, playerId(p));
          return (
            <div key={playerId(p)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: `1px solid ${HAIR}` }}>
              <span style={{ fontSize: 9, fontWeight: 900, color: C.primary, width: 22 }}>{getPos(p)}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <MobilePlayerLink mlbId={getMlb(p)} name={getName(p)} onClick={onPlayerClick} style={{ fontSize: 12, color: C.textHi, fontWeight: 600 }} />
              </span>
              <span style={{ fontSize: 11, color: GREEN }}>{getSal(p)}</span>
              <span onClick={() => togglePlayer(setDeal, p)} style={{ color: inDeal ? GREEN : C.blue, fontSize: 15, cursor: 'pointer', width: 16, textAlign: 'center' }}>{inDeal ? '✓' : '+'}</span>
            </div>
          );
        })}

        <MiniLabel color={C.purpleLighter} style={{ margin: '11px 0 6px' }}>{isGet ? 'Their picks & cash' : 'Your picks & cash'}</MiniLabel>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {picks.map(pk => {
            const inDeal = isPickIn(deal, pickId(pk));
            return <TChip key={pickId(pk)} type="pick" label={`${getPickLabel(pk)}${pickTraded(pk) ? ' ⇄' : ''}`} suffix={inDeal ? ' ✓' : ' +'} onClick={() => togglePick(setDeal, pk)} />;
          })}
          <TChip type="cash" label={isGet ? 'Request FAAB' : 'Add FAAB'} suffix=" +" onClick={() => setCashSheet(side)} />
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <style>{`.tc-ns::-webkit-scrollbar{height:0;width:0;}.tc-ns{scrollbar-width:none;}`}</style>
      <StepRail step={step} />
      {prefillNote && (
        <div data-testid="tc-prefill-note" style={{ margin: '8px 12px 0', padding: '7px 10px', fontSize: 11, lineHeight: 1.4,
          color: C.primary, background: C.primaryTint.t13, border: `1px solid ${C.primary}`, borderRadius: 4 }}>
          {prefillNote}
        </div>
      )}

      {/* ---- STEP BODIES ---- */}
      {step === 1 && (
        <div style={{ padding: '11px 12px' }}>
          <div style={{ ...searchBox, marginBottom: 11 }}>
            <SearchIcon />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search teams…" style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: C.textHi, fontSize: 11 }} />
          </div>
          {teams.filter(t => !t.is_user_team && (!search || (t.team_name || '').toLowerCase().includes(search.toLowerCase()))).map(t => {
            const sel = t.team_id === selectedTeamId;
            return (
              <div key={t.team_id} onClick={() => { setSelectedTeamId(t.team_id); setIGet({ players: [], picks: [], cash: 0 }); setCashGet(0); setAnalysis(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', margin: '0 -10px', borderTop: `1px solid ${HAIR}`, cursor: 'pointer', background: sel ? C.primaryTint.t13 : 'transparent' }}>
                <MobileTeamLogo name={t.team_name} logoUrl={t.team_logo_url} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.textHi, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.team_name}</div>
                  {(t.manager_name || t.rank) && <div style={{ fontSize: 9, color: C.muted }}>{[t.manager_name, t.rank].filter(Boolean).join(' · ')}</div>}
                </div>
                <span style={{ fontSize: 14, color: sel ? C.primary : C.muted }}>›</span>
              </div>
            );
          })}
        </div>
      )}
      {step === 2 && <GiveGetStep side="get" />}
      {step === 3 && <GiveGetStep side="give" />}
      {step === 4 && (
        <div>
          <div style={{ display: 'flex', gap: 8, padding: '12px 12px 0' }}>
            <div style={{ flex: 1 }}>
              <MiniLabel color={RED} style={{ marginBottom: 4 }}>You Give</MiniLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {iGive.players.map(p => <TChip key={playerId(p)} type="player" label={`${getPos(p)} ${getName(p)} ${getSal(p)}`} />)}
                {iGive.picks.map(pk => <TChip key={pickId(pk)} type="pick" label={getPickLabel(pk)} />)}
                {parseInt(cashGive) > 0 && <TChip type="cash" label={`$${cashGive} FAAB`} />}
                {!giveCount && <span style={{ fontSize: 10, color: C.muted }}>—</span>}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <MiniLabel color={GREEN} style={{ marginBottom: 4 }}>You Get</MiniLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {iGet.players.map(p => <TChip key={playerId(p)} type="player" label={`${getPos(p)} ${getName(p)} ${getSal(p)}`} />)}
                {iGet.picks.map(pk => <TChip key={pickId(pk)} type="pick" label={getPickLabel(pk)} />)}
                {parseInt(cashGet) > 0 && <TChip type="cash" label={`$${cashGet} FAAB`} />}
                {!getCount && <span style={{ fontSize: 10, color: C.muted }}>—</span>}
              </div>
            </div>
          </div>
          <div style={{ padding: '12px 12px 0' }}>
            <button onClick={runAnalyze} disabled={!hasAssets || !selectedTeamId} style={{
              width: '100%', padding: '8px 0', borderRadius: 4, fontSize: 11, fontWeight: 800, cursor: 'pointer',
              background: withAlpha(C.blue, 0.12), border: `1px solid ${withAlpha(C.blue, 0.35)}`, color: C.blue,
              opacity: (!hasAssets || !selectedTeamId) ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /><path d="M17 7h4v4" /></svg>
              Analyze Trade
            </button>
          </div>
          <div style={{ padding: '12px 12px' }}>
            <textarea value={notes} onChange={e => setNotes(e.target.value.slice(0, 300))} maxLength={300} rows={2} placeholder="Add a note (optional)…" style={{
              width: '100%', boxSizing: 'border-box', background: PANEL, color: C.textHi, border: `1px solid ${BORDER}`,
              borderRadius: 4, fontSize: 11, padding: '6px 9px', resize: 'none', fontFamily: 'inherit',
            }} />
          </div>
        </div>
      )}

      <TradeAnalyzerSheet open={analyzerOpen} loading={analyzing} analysis={analysis} onClose={() => setAnalyzerOpen(false)} />

      {/* ---- FOOTER ---- */}
      <WizFooter>
        {step > 1 && <GhostBtn onClick={() => setStep(s => s - 1)}>‹ Back</GhostBtn>}
        {step === 1 && <span style={{ fontSize: 10, color: C.muted }}>{selectedTeam ? `${selectedTeam.team_name} selected` : 'Choose a team'}</span>}
        <span style={{ flex: 1 }} />
        {step === 1 && <PrimaryBtn onClick={() => setStep(2)} disabled={!selectedTeamId}>Next: They Give ›</PrimaryBtn>}
        {step === 2 && <PrimaryBtn onClick={() => setStep(3)}>Next: You Give ›</PrimaryBtn>}
        {step === 3 && <PrimaryBtn onClick={() => setStep(4)} disabled={!hasAssets}>Next: Review ›</PrimaryBtn>}
        {step === 4 && <PrimaryBtn onClick={handlePropose} disabled={!hasAssets || submitting}>{submitting ? 'Proposing…' : 'Propose Trade'}</PrimaryBtn>}
      </WizFooter>

      {/* Cash sheet */}
      {cashSheet && (
        <CashSheet
          title={cashSheet === 'get' ? 'Request FAAB cash' : 'Add FAAB cash'}
          available={cashSheet === 'get' ? theirCash : myCash}
          value={cashSheet === 'get' ? cashGet : cashGive}
          onChange={(v) => (cashSheet === 'get' ? setCashGet(v) : setCashGive(v))}
          onConfirm={() => setCashSheet(null)}
          onClose={() => setCashSheet(null)}
        />
      )}

      {/* Propose-side placement: slot-picker per incoming player I receive. */}
      {placementCascade.active && iGet.players[placementCascade.currentIdx] && (
        <MobileSlotPicker
          player={iGet.players[placementCascade.currentIdx]}
          league={league}
          currentRoster={myRoster || []}
          stepLabel={iGet.players.length > 1 ? `${placementCascade.currentIdx + 1} of ${iGet.players.length}` : null}
          committing={submitting}
          onConfirm={handleCascadeConfirm}
          onClose={handleCascadeClose}
        />
      )}
    </div>
  );
};

// ============================================================================
// Trade card (Inbox + Sent) — flush editorial, left-rule via MobileTableSectionHeader
// ============================================================================
// Enriched trade assets are canonical players (get_player_for_trade_display) — render
// with the shared canonical accessors (info/financial/ids), same as the wizard.
const tradeAsset = (gives) => (
  <>
    {(gives?.players || []).map((p, i) => <TChip key={`p${i}`} type="player" label={`${getPos(p)} ${getName(p)} ${getSal(p)}`} />)}
    {(gives?.picks || []).map((pk, i) => <TChip key={`k${i}`} type="pick" label={getPickLabel(pk)} />)}
    {(gives?.cash > 0) && <TChip type="cash" label={`$${gives.cash} FAAB`} />}
  </>
);

const TradeRow = ({ leftLabel, leftColor, leftGives, rightLabel, rightColor, rightGives }) => (
  <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
    <div style={{ flex: 1 }}>
      <MiniLabel color={leftColor} style={{ fontSize: 8.5, marginBottom: 4 }}>{leftLabel}</MiniLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>{tradeAsset(leftGives)}</div>
    </div>
    <div style={{ flex: 1, paddingLeft: 8, borderLeft: `1px solid ${HAIR}` }}>
      <MiniLabel color={rightColor} style={{ fontSize: 8.5, marginBottom: 4 }}>{rightLabel}</MiniLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>{tradeAsset(rightGives)}</div>
    </div>
  </div>
);

// ============================================================================
// TAB 2: INBOX
// ============================================================================
const InboxTab = ({ leagueId, trades, onRefresh, userTeamId, league, teams }) => {
  const [actioning, setActioning] = useState(null);
  const [analysis, setAnalysis] = useState(null);       // analyzer sheet (single, active trade)
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzerOpen, setAnalyzerOpen] = useState(false);
  const [acceptCascade, setAcceptCascade] = useState({ active: false, trade: null, incomingPlayers: [], currentIdx: 0, accumulated: [], myRoster: null, capacityAnalysis: null });

  const submitAcceptWithPlacements = async (tradeId, placements) => {
    setActioning(tradeId);
    try {
      await leaguesAPI.respondToTrade(leagueId, tradeId, 'accept', null, false, placements && placements.length > 0 ? placements : null);
      onRefresh();
    } catch (err) { console.error(err); } finally { setActioning(null); }
  };

  const handleRespond = async (tradeId, action) => {
    if (action === 'reject') {
      const reason = window.prompt('Rejection reason (optional):');
      setActioning(tradeId);
      try { await leaguesAPI.respondToTrade(leagueId, tradeId, action, reason || null); onRefresh(); }
      catch (err) { console.error(err); } finally { setActioning(null); }
      return;
    }
    const trade = trades.find(t => t.trade_id === tradeId);
    // they_give = the canonical players I RECEIVE on accept (proposals response key).
    const incomingPlayers = trade?.they_give?.players ?? [];
    if (incomingPlayers.length === 0) { await submitAcceptWithPlacements(tradeId, null); return; }
    if (!userTeamId) { console.warn('No userTeamId for accept-cascade'); return; }
    try {
      const rosterResp = await leaguesAPI.getTeamRosterCanonical(leagueId, userTeamId);
      const myRoster = (rosterResp?.players || []).filter(p => !p.accrued_info);
      setAcceptCascade({ active: true, trade, incomingPlayers, currentIdx: 0, accumulated: [], myRoster });
    } catch (err) { console.error('Failed to load roster for mobile accept cascade:', err); }
  };

  const handleAcceptCascadeConfirm = async (option) => {
    const incoming = acceptCascade.incomingPlayers[acceptCascade.currentIdx];
    const placement = buildPlacement(incoming, option);
    const newAccumulated = [...acceptCascade.accumulated, placement];
    const nextIdx = acceptCascade.currentIdx + 1;
    if (nextIdx >= acceptCascade.incomingPlayers.length) {
      const tradeId = acceptCascade.trade?.trade_id;
      setAcceptCascade({ active: false, trade: null, incomingPlayers: [], currentIdx: 0, accumulated: [], myRoster: null });
      if (tradeId) await submitAcceptWithPlacements(tradeId, newAccumulated);
    } else {
      setAcceptCascade(prev => ({ ...prev, currentIdx: nextIdx, accumulated: newAccumulated }));
    }
  };
  const handleAcceptCascadeClose = () => setAcceptCascade({ active: false, trade: null, incomingPlayers: [], currentIdx: 0, accumulated: [], myRoster: null, capacityAnalysis: null });

  // User-triggered analyzer sheet (per proposal). Analyze from MY (receiver) side:
  // I give they_want, I get they_give. Enriched assets hold canonical objects →
  // extract id lists for the endpoint (canonical ids/pick_id namespaces).
  const handleAnalyze = async (trade) => {
    setAnalysis(null);
    setAnalyzing(true);
    setAnalyzerOpen(true);
    try {
      const extractIds = (side) => ({
        players: (side?.players ?? []).map(p => p.ids?.league_player).filter(Boolean),
        picks: (side?.picks ?? []).map(pk => ({ pick_id: pk.pick_id, pick_type: pk.pick_type ?? 'regular' })),
        cash: side?.cash ?? 0,
      });
      const res = await leaguesAPI.analyzeTrade(leagueId, {
        receiving_team_id: trade.other_team?.team_id,
        proposing_team_gives: extractIds(trade.they_want),
        receiving_team_gives: extractIds(trade.they_give),
      });
      setAnalysis(res);
    } catch (err) { console.error(err); setAnalysis({ success: false, detail: 'Trade analysis failed.' }); }
    finally { setAnalyzing(false); }
  };

  if (!trades.length) return <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 12 }}>No incoming trade proposals</div>;

  const actBtn = (onClick, color, label, disabled) => (
    <button onClick={onClick} disabled={disabled} style={{ flex: 1, padding: '6px 0', borderRadius: 4, fontSize: 10, fontWeight: 800, cursor: disabled ? 'default' : 'pointer', background: withAlpha(color, 0.1), border: `1px solid ${withAlpha(color, 0.3)}`, color }}>{label}</button>
  );

  return (
    <div style={{ padding: '4px 12px 12px' }}>
      {trades.map(trade => {
        const tid = trade.trade_id;
        return (
          <div key={tid} style={{ padding: '8px 0', borderBottom: `1px solid ${HAIR}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MobileTeamLogo name={trade.other_team?.team_name} logoUrl={(teams.find(t => t.team_id === trade.other_team?.team_id) || {}).team_logo_url} size={18} />
                <span style={{ fontSize: 11, fontWeight: 700, color: C.textHi }}>From {trade.other_team?.team_name || 'Unknown'}</span>
              </span>
              <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 2, background: withAlpha(RED, 0.2), color: C.spark.down }}>Action needed</span>
            </div>
            <TradeRow leftLabel="They Give" leftColor={GREEN} leftGives={trade.they_give} rightLabel="They Want" rightColor={RED} rightGives={trade.they_want} />
            {trade.notes && <div style={{ fontSize: 9, color: C.mutedLight, fontStyle: 'italic', marginBottom: 6 }}>"{trade.notes}"</div>}
            <div style={{ display: 'flex', gap: 5, marginTop: 4 }}>
              {actBtn(() => handleAnalyze(trade), C.blue, 'Analyze', false)}
              {actBtn(() => handleRespond(tid, 'accept'), GREEN, 'Accept', actioning === tid)}
              {actBtn(() => handleRespond(tid, 'reject'), RED, 'Reject', actioning === tid)}
            </div>
          </div>
        );
      })}

      <TradeAnalyzerSheet open={analyzerOpen} loading={analyzing} analysis={analysis} onClose={() => setAnalyzerOpen(false)} />

      {/* Accept-side placement: slot-picker per incoming player I receive. */}
      {acceptCascade.active && acceptCascade.incomingPlayers[acceptCascade.currentIdx] && (
        <MobileSlotPicker
          player={acceptCascade.incomingPlayers[acceptCascade.currentIdx]}
          league={league}
          currentRoster={acceptCascade.myRoster || []}
          stepLabel={acceptCascade.incomingPlayers.length > 1 ? `${acceptCascade.currentIdx + 1} of ${acceptCascade.incomingPlayers.length}` : null}
          committing={actioning === acceptCascade.trade?.trade_id}
          onConfirm={handleAcceptCascadeConfirm}
          onClose={handleAcceptCascadeClose}
        />
      )}
    </div>
  );
};

// ============================================================================
// TAB 3: SENT
// ============================================================================
const SentTab = ({ leagueId, trades, onRefresh, teams }) => {
  const [cancelling, setCancelling] = useState(null);
  const handleCancel = async (tradeId) => {
    setCancelling(tradeId);
    try { await leaguesAPI.cancelTrade(leagueId, tradeId); onRefresh(); }
    catch (err) { console.error(err); } finally { setCancelling(null); }
  };
  if (!trades.length) return <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 12 }}>No outgoing trade proposals</div>;
  return (
    <div style={{ padding: '4px 12px 12px' }}>
      {trades.map(trade => (
        <div key={trade.trade_id} style={{ padding: '8px 0', borderBottom: `1px solid ${HAIR}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MobileTeamLogo name={trade.other_team?.team_name} logoUrl={(teams.find(t => t.team_id === trade.other_team?.team_id) || {}).team_logo_url} size={18} />
              <span style={{ fontSize: 11, fontWeight: 700, color: C.textHi }}>To {trade.other_team?.team_name || 'Unknown'}</span>
            </span>
            <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 2, background: withAlpha(C.blue, 0.2), color: C.blue }}>Awaiting response</span>
          </div>
          <TradeRow leftLabel="You Give" leftColor={RED} leftGives={trade.i_give} rightLabel="You Get" rightColor={GREEN} rightGives={trade.i_receive} />
          {trade.notes && <div style={{ fontSize: 9, color: C.mutedLight, fontStyle: 'italic', marginBottom: 6 }}>"{trade.notes}"</div>}
          <button onClick={() => handleCancel(trade.trade_id)} disabled={cancelling === trade.trade_id} style={{
            width: '100%', padding: '6px 0', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer',
            background: PANEL, border: `1px solid ${BORDER}`, color: C.muted,
          }}>{cancelling === trade.trade_id ? 'Cancelling…' : 'Cancel'}</button>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN
// ============================================================================
function MobileTradeCenter({ leagueId, userTeamId, initialPackage, onPrefillConsumed }) {
  const [activeTab, setActiveTab] = useState('propose');
  // DD-MOBILE-OPTIMIZER-ACTIONS-0724: an optimizer deep-link opens on the Propose tab.
  useEffect(() => { if (initialPackage) setActiveTab('propose'); }, [initialPackage]);
  const [teams, setTeams] = useState([]);
  const [trades, setTrades] = useState({ proposed_to_me: [], proposed_by_me: [] });
  const [tradesLoading, setTradesLoading] = useState(true);
  const [league, setLeague] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [activePlayerId, setActivePlayerId] = useState(null);

  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getLeagueTeams(leagueId)
      .then(res => {
        if (res?.success && res.teams) {
          setTeams(res.teams.map(t => ({
            team_id: t.team_id, team_name: t.team_name || 'Team', is_user_team: t.is_user_team || false,
            team_logo_url: t.team_logo_url || null,
            manager_name: t.manager_name || t.owner_name || null, rank: t.rank ? `${t.rank}${t.rank === 1 ? 'st' : t.rank === 2 ? 'nd' : t.rank === 3 ? 'rd' : 'th'}` : null,
          })));
        }
      })
      .catch(() => {});
  }, [leagueId]);

  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getLeagueDetails(leagueId).then(res => setLeague(res?.league ?? null)).catch(() => setLeague(null));
  }, [leagueId]);

  const fetchTrades = useCallback(async () => {
    if (!leagueId) return;
    setTradesLoading(true);
    try {
      const res = await leaguesAPI.getMyTradeProposals(leagueId);
      setTrades({ proposed_to_me: res?.proposed_to_me || [], proposed_by_me: res?.proposed_by_me || [] });
    } catch { setTrades({ proposed_to_me: [], proposed_by_me: [] }); }
    finally { setTradesLoading(false); }
  }, [leagueId]);
  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const handlePlayerClick = useCallback((mlbId) => { if (mlbId) { setActivePlayerId(mlbId.toString()); setIsModalOpen(true); } }, []);
  const handleModalClose = () => { setIsModalOpen(false); setTimeout(() => setActivePlayerId(null), 300); };
  const handleExpandToCard = () => { setIsModalOpen(false); setIsCardOpen(true); };
  const handleCollapseToModal = () => { setIsCardOpen(false); setIsModalOpen(true); };
  const handleCloseAll = () => { setIsCardOpen(false); setIsModalOpen(false); setTimeout(() => setActivePlayerId(null), 300); };

  const inboxCount = trades.proposed_to_me.length;
  const sentCount = trades.proposed_by_me.length;
  const TABS = [
    { id: 'propose', label: 'Propose', count: null, color: null },
    { id: 'inbox', label: 'Inbox', count: inboxCount, color: C.spark.down },
    { id: 'sent', label: 'Sent', count: sentCount, color: C.muted },
  ];

  return (
    <div>
      {/* Primary tabs (.ptabs) — Propose · Inbox · Sent */}
      <div style={{ display: 'flex', gap: 14, padding: '7px 14px 0', borderBottom: `1px solid ${BORDER}` }}>
        {TABS.map(tab => {
          const on = activeTab === tab.id;
          return (
            <span key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id !== 'propose') fetchTrades(); }}
              style={{ position: 'relative', fontSize: 12, fontWeight: 800, paddingBottom: 5, cursor: 'pointer', color: on ? C.primary : C.muted }}>
              {tab.label}{tab.count > 0 && <span style={{ color: tab.color, marginLeft: 4 }}>{tab.count}</span>}
              {on && <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, borderRadius: 2, background: C.primary }} />}
            </span>
          );
        })}
      </div>

      {activeTab === 'propose' && (
        <ProposeWizard leagueId={leagueId} userTeamId={userTeamId} teams={teams} onPlayerClick={handlePlayerClick} league={league}
          initialPackage={initialPackage} onPrefillConsumed={onPrefillConsumed} />
      )}
      {activeTab === 'inbox' && (
        tradesLoading ? <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 12 }}>Loading…</div>
          : <InboxTab leagueId={leagueId} trades={trades.proposed_to_me} onRefresh={fetchTrades} userTeamId={userTeamId} league={league} teams={teams} />
      )}
      {activeTab === 'sent' && (
        tradesLoading ? <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 12 }}>Loading…</div>
          : <SentTab leagueId={leagueId} trades={trades.proposed_by_me} onRefresh={fetchTrades} teams={teams} />
      )}

      <MobilePlayerModal playerId={activePlayerId} isOpen={isModalOpen} onClose={handleModalClose} onExpandToCard={handleExpandToCard} leagueId={leagueId} />
      <MobilePlayerCard playerId={activePlayerId} leagueId={leagueId} isOpen={isCardOpen} onClose={handleCloseAll} onCollapse={handleCollapseToModal} />
    </div>
  );
}

export default MobileTradeCenter;
